import { useEffect, useRef, useState } from 'react';
import { ArrowDownToLine, ArrowRight, CircleAlert, ExternalLink, LoaderCircle } from 'lucide-react';
import { Button, buttonVariants } from './ui/button';
import { BlockingScreen, SUPPORT_URL } from './platform-notices';
import { useLinks } from '@/lib/party';
import { useUpdateRequired } from '@/lib/updates';

/**
 * Blocks the whole app when this copy is older than the server's MIN_DESKTOP_VERSION. It can't be
 * closed: the only way out is updating, through the same updater the update banner uses. There's no
 * Sign out here, as the server turns away everything from a copy this old.
 */
export function MandatoryUpdate() {
  const u = useUpdateRequired();
  const downloadUrl = useLinks().downloadUrl || 'https://app.teaplus.tv/download';
  const [updating, setUpdating] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const status = u?.state.status;
  const required = !!u;

  // Look for the update straight away rather than waiting for the next scheduled check.
  useEffect(() => {
    if (required && (status === 'idle' || status === 'up-to-date')) void u?.check();
    // Only on first showing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  // Once the user asked to update, restart as soon as the download is in.
  useEffect(() => {
    if (updating && status === 'ready') void u?.install();
  }, [updating, status, u]);

  // Nothing behind the screen can be reached, and a fullscreen player would hide it.
  useEffect(() => {
    if (!required) return;
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    document.querySelectorAll('video').forEach((v) => v.pause());
    button.current?.focus();
    return () => root?.removeAttribute('inert');
  }, [required]);

  if (!u) return null;
  const { state } = u;
  const busy =
    state.status === 'downloading' ||
    state.status === 'reinstalling' ||
    (updating && state.status === 'ready');
  const checking = state.status === 'checking';

  const update = () => {
    setUpdating(true);
    if (state.status === 'available') void u.download();
    else if (state.status === 'ready') void u.install();
    // No update offered by the feed (or it failed): fetch and run the latest installer directly.
    else void u.reinstall();
  };

  return (
    <BlockingScreen
      id="mandatory-update"
      className="z-[200]"
      icon={<ArrowDownToLine className="size-6" aria-hidden />}
      iconClassName="bg-pink/10 text-pink"
      title="Update required"
      body={
        <>
          This version of Tea+ is out of date and can no longer be used. Update to the latest
          version to carry on. It only takes a moment, and Tea+ reopens when it&rsquo;s done.
        </>
      }
    >
      <div className="desktop-titlebar" aria-hidden />
      <div className="mx-auto mt-6 flex max-w-sm items-center gap-3 rounded-xl border border-white/10 bg-bg-2 px-4 py-2 text-left">
        <span className="min-w-0 flex-1">
          <span className="block text-xs text-muted">Your version</span>
          <span className="block truncate font-mono text-sm text-text">{state.current}</span>
        </span>
        <ArrowRight className="size-4 shrink-0 text-muted" aria-hidden />
        <span className="min-w-0 flex-1 text-right">
          <span className="block text-xs text-muted">Required version</span>
          <span className="block truncate font-mono text-sm text-pink">{u.min}</span>
        </span>
      </div>

      <div className="mt-8" aria-live="polite">
        {busy ? (
          <div className="mx-auto max-w-sm text-left">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="flex items-center gap-2 text-muted">
                <LoaderCircle className="size-4 shrink-0 animate-spin text-pink" />
                {state.status === 'ready'
                  ? 'Restarting Tea+…'
                  : 'Downloading the update… Tea+ restarts when it’s done.'}
              </span>
              <span className="tabular-nums">{state.progress ?? 0}%</span>
            </div>
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/8">
              <div
                className="h-full bg-pink transition-[width] duration-300"
                style={{ width: `${state.progress ?? 0}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            {state.status === 'error' && (
              <p className="mx-auto mb-4 flex max-w-sm items-start gap-2 text-left text-sm text-warning">
                <CircleAlert className="mt-0.5 size-4 shrink-0" /> {state.error}
              </p>
            )}
            <div className="flex flex-wrap justify-center gap-3">
              <Button ref={button} onClick={update} loading={checking} disabled={checking}>
                {!checking && <ArrowDownToLine className="size-4" />}
                {checking
                  ? 'Finding the update…'
                  : state.status === 'error'
                    ? 'Try updating again'
                    : 'Update now'}
              </Button>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'secondary' })}
              >
                <ExternalLink className="size-4" />
                Download it instead
              </a>
              <Button
                variant="secondary"
                disabled={rechecking || checking}
                onClick={async () => {
                  setRechecking(true);
                  await Promise.all([u.recheck(), u.check()]).catch(() => undefined);
                  setRechecking(false);
                }}
              >
                {rechecking && <LoaderCircle className="size-4 animate-spin" />}
                Check again
              </Button>
            </div>
          </>
        )}
      </div>
      <a
        href={SUPPORT_URL}
        target="_blank"
        rel="noreferrer"
        className="mx-auto mt-6 block w-fit text-sm text-muted hover:text-text"
      >
        Having trouble? Contact Tea+ Support
      </a>
    </BlockingScreen>
  );
}
